import { columnLabels, kundPlacesOf, kunds } from "@repo/vihangam-yog-shared";
import { useMemo, useState } from "react";

import { isNotComing } from "../event-registrations/notComing";
import type { Registration } from "../event-registrations/shared";

/** Whether a booking is through the door: all of it, some of it, or none. */
function arrivalOf(r: Registration): "here" | "part" | "not-yet" {
  if (r.checkedInAt) {
    return "here";
  }
  return r.party.some((m) => m.arrivedAt) ? "part" : "not-yet";
}

const CELL_TONE = {
  here: "border-emerald-700 bg-emerald-700 text-white",
  part: "border-2 border-emerald-700 bg-[color:var(--color-paper)] text-[color:var(--color-ink)]",
  "not-yet":
    "border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] text-[color:var(--color-ink)]",
} satisfies Record<ReturnType<typeof arrivalOf>, string>;

const ARRIVAL_WORDS = {
  here: "here",
  part: "some here",
  "not-yet": "not here yet",
} satisfies Record<ReturnType<typeof arrivalOf>, string>;

/**
 * The Hawan ground on the Reception page: every Kund with who sits there,
 * filled in as they arrive, so the desk can see the room fill up and send a
 * runner the right way. Tap a Kund to open that booking. Drawn only once
 * someone has a Kund.
 */
export function KundGround({
  registrations,
  onOpenGuest,
}: {
  registrations: readonly Registration[];
  onOpenGuest?: (r: Registration) => void;
}) {
  const [shown, setShown] = useState(true);
  const byPlace = useMemo(() => {
    const m = new Map<string, Registration>();
    for (const r of registrations) {
      if (isNotComing(r)) {
        continue;
      }
      for (const place of kundPlacesOf(r.kundPlace)) {
        m.set(place, r);
      }
    }
    return m;
  }, [registrations]);
  if (byPlace.size === 0) {
    return null;
  }
  const here = [...byPlace.values()].filter((r) => arrivalOf(r) !== "not-yet");
  const kundsHere = [...byPlace].filter(
    ([, r]) => arrivalOf(r) !== "not-yet",
  ).length;
  return (
    <section
      aria-label="Kund map"
      className="mt-6 rounded-xl border border-[color:var(--color-line)] bg-[color:var(--color-paper)] p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-[18px] font-bold text-[color:var(--color-ink)]">
            Kund map
          </h2>
          <p className="text-[15px] text-[color:var(--color-ink-soft)] tabular-nums">
            {kundsHere} of {byPlace.size} Kunds have someone here
            {here.length === 0 ? "" : ". Green is here."}
          </p>
        </div>
        <button
          type="button"
          aria-expanded={shown}
          onClick={() => setShown((s) => !s)}
          className="inline-flex min-h-11 items-center rounded-md border border-[color:var(--color-line-strong)] px-4 text-[15px] font-semibold text-[color:var(--color-ink)]"
        >
          {shown ? "Hide map" : "Show map"}
        </button>
      </div>
      {shown ? (
        <div className="mt-3">
          <p className="rounded-md bg-[color:var(--color-line)] py-1.5 text-center text-[12px] font-semibold tracking-[0.3em] text-[color:var(--color-ink-soft)]">
            STAGE
          </p>
          {/* People come in at the front left, beside A1. */}
          <p className="mt-2 text-[12px] tracking-[0.2em] text-[color:var(--color-ink-soft)]">
            ENTRANCE, BY A1 ↓
          </p>
          <div className="mt-2 grid grid-cols-4 items-start gap-1.5">
            {columnLabels.map((column) => (
              <div key={column.label} className="flex flex-col gap-1.5">
                <span
                  aria-hidden="true"
                  className="text-center text-[13px] font-semibold text-[color:var(--color-ink-soft)]"
                >
                  {column.label}
                </span>
                {kunds
                  .filter((kund) => kund.column === column.label)
                  .map((kund) => {
                    const holder = byPlace.get(kund.code);
                    if (!holder) {
                      return (
                        <span
                          key={kund.code}
                          className="flex min-h-11 items-center justify-center rounded-md border border-dashed border-[color:var(--color-line-strong)] text-[13px] text-[color:var(--color-ink-soft)]"
                        >
                          {kund.code}
                        </span>
                      );
                    }
                    const arrival = arrivalOf(holder);
                    const first =
                      holder.name.trim().split(/\s+/)[0] ?? holder.name;
                    return (
                      <button
                        key={kund.code}
                        type="button"
                        disabled={!onOpenGuest}
                        aria-label={`Kund ${kund.code}, ${holder.name}, ${ARRIVAL_WORDS[arrival]}`}
                        onClick={() => onOpenGuest?.(holder)}
                        className={`flex min-h-11 flex-col items-center justify-center rounded-md border px-1 leading-tight ${CELL_TONE[arrival]}`}
                      >
                        <span className="text-[13px] font-bold tabular-nums">
                          {kund.code}
                        </span>
                        <span className="max-w-full truncate text-[12px]">
                          {first}
                        </span>
                      </button>
                    );
                  })}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
